import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { ClientAwarenessLink } from '@apollo/client/link/client-awareness';
import generatedPossibleTypes from '__generated__/zeusPossibleTypes.json';
import {
  BLOCKED_RETRY_STATUS_NUMBERS,
  errorReporterLink,
  raiseRetryableGQLErrorsLink,
  readOnlyMutationBlockLink,
  redirectUnauthenticatedErrorsLink,
  retryLink,
  trackTimeToFirstQueryLink,
} from 'platform/apolloLink';
import { globalTrackError } from 'platform/observability';

const { possibleTypes } = generatedPossibleTypes;

type ZeusApolloClientOptions = {
  /** Customer whose graph is read on behalf of an authorized support operator. */
  impersonatedCustomerUUID?: string;
};

export const createZeusApolloClient = (options: ZeusApolloClientOptions = {}): ApolloClient =>
  new ApolloClient({
    link: ApolloLink.from([
      readOnlyMutationBlockLink(),
      new ClientAwarenessLink({
        clientAwareness: {
          name: 'cake',
          version: import.meta.env.ODFE_GIT_SHA || 'local',
        },
      }),
      trackTimeToFirstQueryLink(),
      errorReporterLink({
        includeVariables: false,
        trackError: globalTrackError,
      }),
      redirectUnauthenticatedErrorsLink({ graphQLErrorCodes: ['UNAUTHORIZED'] }),
      retryLink(),
      raiseRetryableGQLErrorsLink(BLOCKED_RETRY_STATUS_NUMBERS),
      new HttpLink({
        uri: options.impersonatedCustomerUUID ? '/api/zeus/customer-support' : '/api/zeus',
        credentials: 'include',
        headers: options.impersonatedCustomerUUID
          ? { 'x-zeus-act-as-customer-uuid': options.impersonatedCustomerUUID }
          : undefined,
      }),
    ]),
    cache: new InMemoryCache({ possibleTypes }),
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
      watchQuery: {
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: false,
      },
      mutate: {
        context: { retry: false },
        errorPolicy: 'all',
      },
    },
  });
